import { useState, useEffect, useRef, useContext } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { registerUserWithAnswers } from "../services/ChatService";
import { UserContext } from "../context/UserContext.jsx";
import { useModal } from "../context/ModalContext";
import { api } from "../api";
import { useToast } from "../context/ToastContext";

import evaluateBaby from "../logic/evaluateBaby";
import evaluateKids from "../logic/evaluateKids";
import evaluateAdults from "../logic/evaluateAdults";
import { rewriteAdultCollectionUrl } from "../utils/anamneseVersion";
import Utils from "../utils/Utils";
import fullLogo from "../assets/images/svg/logoFullVertical.svg";

/**
 * Validação mínima por seção (≥1 resposta; ranges em 0 não contam).
 * Controlada por REACT_APP_ANAMNESIS_MIN_SECTION_ANSWER (default: true).
 * Isenta: sub_id 45, seções com ≤1 pergunta, seções só com ranges opcionais.
 * @returns {{ error: string|null, errorQuestionId: number|string|null }}
 */
function validateMinSectionAnswer({
  sectionId,
  sectionQuestions = [],
  currentGrouped = null,
  answers = {},
  storedAnswers = {},
}) {
  if (process.env.REACT_APP_ANAMNESIS_MIN_SECTION_ANSWER === "false") {
    return { error: null, errorQuestionId: null };
  }

  const COMPLEMENTARY_SECTION_ID = 45;
  const DECORATIVE_TYPES = new Set(["separatorTitle", "categoryTitle"]);
  const RANGE_TYPES = new Set(["range", "text+range"]);

  if (Number(sectionId) === COMPLEMENTARY_SECTION_ID) {
    return { error: null, errorQuestionId: null };
  }

  const sectionQs = (currentGrouped?.questions || sectionQuestions || []).filter(
    (q) => !DECORATIVE_TYPES.has(q.type)
  );

  const onlyOptionalRanges =
    sectionQs.length > 0 &&
    sectionQs.every(
      (q) =>
        RANGE_TYPES.has(q.type) &&
        (q.optional === 1 || q.optional === true)
    );

  if (sectionQs.length <= 1 || onlyOptionalRanges) {
    return { error: null, errorQuestionId: null };
  }

  const isAnswered = (answerData) => {
    if (!answerData) return false;
    if (Array.isArray(answerData)) return answerData.length > 0;
    // check/checkbox: { answer: [...], items: [...] }
    if (Array.isArray(answerData?.answer)) {
      return answerData.answer.length > 0;
    }
    if (Array.isArray(answerData?.items)) {
      return answerData.items.length > 0;
    }
    if (typeof answerData?.answer === "string") {
      return answerData.answer.trim() !== "";
    }
    if (
      typeof answerData?.answer === "number" ||
      typeof answerData?.answer === "boolean"
    ) {
      return answerData.answer !== null && answerData.answer !== undefined;
    }
    if (typeof answerData === "object" && answerData !== null) {
      return Object.keys(answerData).some((key) => !isNaN(Number(key)));
    }
    return false;
  };

  const isNonZeroRange = (answerData) => {
    const value = answerData?.answer;
    if (value === null || value === undefined || value === "") return false;
    const num = Number(value);
    return !Number.isNaN(num) && num !== 0;
  };

  const answerSource = answers || storedAnswers;
  let otherAnswered = 0;
  let nonZeroRanges = 0;

  for (const q of sectionQs) {
    const answerData = answerSource[q.sym_id];
    if (RANGE_TYPES.has(q.type)) {
      if (isNonZeroRange(answerData)) nonZeroRanges += 1;
    } else if (isAnswered(answerData)) {
      otherAnswered += 1;
    }
  }

  if (otherAnswered < 1 && nonZeroRanges < 1) {
    return {
      error: "Responda ao menos uma pergunta desta seção para continuar.",
      errorQuestionId: sectionQs[0]?.sym_id ?? null,
    };
  }

  return { error: null, errorQuestionId: null };
}

const useChatLogic = (grupoSlug = null) => {
  const { openModal, closeModal } = useModal();
  const { toast } = useToast();
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [groupedQuestions, setGroupedQuestions] = useState([]);
  const [answers, setAnswers] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("userAnswers")) || {};
    } catch {
      return {};
    }
  });
  // Inicializa o índice de acordo com sectionId, se possível
  const [currentSectionIndex, setCurrentSectionIndex] = useState(() => 0);
  const [currentSectionQuestions, setCurrentSectionQuestions] = useState([]);
  const [nextSectionQuestions, setNextSectionQuestions] = useState([]);
  const [currentGrouped, setCurrentGrouped] = useState(null);
  const [isChatFinished, setIsChatFinished] = useState(false);
  // const [userData, setUserData] = useState(() => {
  //   return JSON.parse(localStorage.getItem("userData")) || {};
  // });
  // const { usr_id, usr_name, usr_email, customer_id } = userData;

  const [weightLoss, setWeightLoss] = useState(false);
  const [performance, setPerformance] = useState(false);

  const [analyzing, setAnalyzing] = useState(false);

  const [progress, setProgress] = useState(0);
  const [sectionOrder, setSectionOrder] = useState([]);

  const sectionSpendingTimeRef = useRef(0);

  const stripHTML = (html) => {
    const doc = new DOMParser().parseFromString(html || "", "text/html");
    return doc.body.textContent || "";
  };

  const currentSectionMeta =
    currentSectionQuestions?.length > 0
      ? {
          groupTitle: currentSectionQuestions[0].groupTitle,
          sub_id: currentSectionQuestions[0].sub_id,
          nextGroupTitle: stripHTML(nextSectionQuestions?.[0]?.groupTitle),
          groupDescription: currentSectionQuestions[0].groupDescription,
          groupIcon: currentSectionQuestions[0].groupIcon,
          groupCover: currentSectionQuestions[0].groupCover,
        }
      : null;

  const applySexFilter = (questions, grouped, sectionOrder, sexo) => {
    if (sexo === 189) {
      return {
        questions: questions.filter((q) => q.sub_id !== 29),
        grouped: grouped.filter((g) => g.sub_id !== 29),
        sectionOrder: sectionOrder.filter((id) => id !== 29),
      };
    } else if (sexo === 190) {
      return {
        questions: questions.filter((q) => q.sub_id !== 28),
        grouped: grouped.filter((g) => g.sub_id !== 28),
        sectionOrder: sectionOrder.filter((id) => id !== 28),
      };
    }
    return { questions, grouped, sectionOrder };
  };

  const fetchQuestions = async () => {
    try {
      const url = grupoSlug
        ? `symptoms?grupo=${grupoSlug}`
        : `symptoms`;

      const response = await api.get(url);
      const data = response.data?.data || response.data;

      if (data && data.status === 1 && data.anamnese) {
        const flatQuestions = [];
        const groupedData = [];
        const orderedSubIds = [];

        data.anamnese.forEach((group) => {
          orderedSubIds.push(group.sub_id);

          const symGroupMap = {};

          group.questions
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .forEach((q) => {
              if (
                q.sym_group &&
                (q.type === "rangeGroup" || q.type === "text+range")
              ) {
                if (!symGroupMap[q.sym_group]) symGroupMap[q.sym_group] = [];
                symGroupMap[q.sym_group].push(q);
              } else {
                flatQuestions.push({
                  ...q,
                  sym_group: q.sym_group || null,
                  hidden: group.group === 4 && q.sym_id !== 264,
                  sub_id: group.sub_id,
                  groupTitle: group.title,
                  groupDescription: group.description,
                  groupIcon: group.icon,
                  groupCover: group.cover,
                  groupId: group.group,
                });
              }
            });

          // após o forEach, processa os slidersGroup
          Object.entries(symGroupMap).forEach(([groupKey, items]) => {
            const header = items.find((q) => q.type === "title");
            const sliders = items.filter(
              (q) => q.type === "rangeGroup" || q.type === "text+range"
            );

            if (sliders.length > 0) {
              flatQuestions.push({
                sym_id: header?.sym_id || sliders[0].sym_id,
                sub_id: group.sub_id,
                groupTitle: group.title,
                groupDescription: group.description,
                groupIcon: group.icon,
                groupCover: group.cover,
                groupId: group.group,
                label: header?.label || group.title,
                description: header?.sublabel || "",
                type: "slidersGroup",
                groupItems: sliders.map((s) => ({
                  ...s,
                  sym_group: s.sym_group || null,
                })),
                order: header?.order || sliders[0]?.order || 0,
              });
            }
          });
        });

        const subjectMap = {};
        flatQuestions.forEach((q) => {
          if (q.groupTitle) {
            subjectMap[q.sub_id] = q.groupTitle;
          }
        });
        localStorage.setItem("subjectsMeta", JSON.stringify(subjectMap));

        flatQuestions.sort((a, b) => (a.order || 0) - (b.order || 0));

        // Aplicar filtro de sexo salvo (sym_id 254) após reload
        let sexo = null;
        try {
          const storedAnswers = JSON.parse(localStorage.getItem("userAnswers") || "{}");
          sexo = storedAnswers?.[254]?.answer;
        } catch {}

        const { questions, grouped, sectionOrder } = applySexFilter(
          flatQuestions,
          groupedData,
          orderedSubIds,
          sexo
        );

        setQuestions(questions);
        setGroupedQuestions(grouped);
        setSectionOrder(sectionOrder);
        setIsLoaded(true);
      }
    } catch (err) {
      console.error("Erro ao carregar perguntas:", err);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [grupoSlug]);

  // Sincronizar currentSectionIndex com sectionId da URL ao carregar perguntas e sectionOrder
  useEffect(() => {
    if (Array.isArray(sectionOrder) && sectionOrder.length > 0) {
      if (sectionId) {
        const idx = sectionOrder.findIndex(id => String(id) === String(sectionId));
        if (idx >= 0 && idx !== currentSectionIndex) {
          setCurrentSectionIndex(idx);
        }
      } else {
        // Sem sectionId na URL (ex.: entrada por "/questionario/:grupo"): redireciona
        // imediatamente para a rota com :sectionId. Isso evita que o Chat seja montado
        // primeiro na rota sem sectionId (que passa por SalesSuspendedRoute) e depois,
        // ao avançar de seção, seja remontado na rota com :sectionId (que não passa por
        // SalesSuspendedRoute) — remontagem essa que zera estados locais do Chat (ex.:
        // aplv) e deixava o botão "Avançar" preso como desabilitado na 2ª seção.
        if (currentSectionIndex !== 0) setCurrentSectionIndex(0);
        navigate(`/questionario/${grupoSlug}/${sectionOrder[0]}`, { replace: true });
      }
    }
  }, [sectionId, sectionOrder, grupoSlug, navigate]);

  useEffect(() => {
    // calcular o tempo gasto na seção atual
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      sectionSpendingTimeRef.current = elapsed;
    }, 1000);

    return () => clearInterval(interval);
  }, [currentSectionIndex]);

  useEffect(() => {
  if (!questions.length || !Array.isArray(sectionOrder) || currentSectionIndex >= sectionOrder.length) return;

    const subIdAtual = sectionOrder[currentSectionIndex];
    const subNextId = sectionOrder[currentSectionIndex + 1];

    const debug = questions
      .filter((q) => String(q.sub_id) === String(subIdAtual))
      .map((q) => ({
        sym_id: q.sym_id,
        type: q.type,
        label: q.label,
        groupItems: q.groupItems?.length,
      }));

    const nextNormais = questions.filter(
      (q) => String(q.sub_id) === String(subNextId)
    );

    const todas = questions.filter(
      (q) => String(q.sub_id) === String(subIdAtual)
    );

    const agrupado =
      groupedQuestions.find((g) => String(g.sub_id) === String(subIdAtual)) ||
      null;

  setCurrentSectionQuestions(Array.isArray(todas) ? [...todas, ...(Array.isArray(agrupado?.questions) ? agrupado.questions : [])] : []);
  setNextSectionQuestions(Array.isArray(nextNormais) ? nextNormais : []);
    setCurrentGrouped(agrupado);

    setProgress(
      Array.isArray(sectionOrder) && sectionOrder.length > 0
        ? Math.round((currentSectionIndex / sectionOrder.length) * 100)
        : 0
    );

    // Definir se inclui suplemento de performance ou de perda de peso
    const answer106 = answers[106]?.answer;
    const answer107 = answers[107]?.answer;

    if (answer107 === 65 && !weightLoss) {
      setPerformance(true);
    }

    if (answer106 === 63) {
      if (performance) setPerformance(false);
      setWeightLoss(true);
    }
  }, [questions, groupedQuestions, currentSectionIndex, answers, sectionOrder]);

  const handleChangeAnswer = (sym_id, answer, question) => {
    // console.log("handleChangeAnswer", sym_id, answer, question);
    // Garante persistência correta para checkbox
    let answerValue;
    if (question.type === 'checkbox' || question.type === 'check') {
      // Se vier array, salva como items e answer
      if (Array.isArray(answer)) {
        answerValue = { answer, items: answer };
      } else if (typeof answer === 'object' && answer?.items) {
        answerValue = { ...answer, answer: answer.items };
      } else if (typeof answer === 'object' && answer !== null && Object.keys(answer).some(k => !isNaN(Number(k)))) {
        // Caso seja objeto com chaves numéricas (compatibilidade)
        const arr = Object.values(answer).filter(v => typeof v === 'number');
        answerValue = { answer: arr, items: arr };
      } else {
        answerValue = { answer };
      }
    } else {
      answerValue = typeof answer === "object" ? answer : { answer: answer };
    }
    setAnswers((prev) => {
      const isRange = question.type === 'range';
      const newAnswers = {
        ...prev,
        [sym_id]: {
          ...answerValue,
          group: question.groupId,
          subject: question.sub_id,
          sym_for: question.sym_for,
          sym_group: question.sym_group ?? null,
          required: question.required ?? false,
          type: question.type,
          ...(question.reverse !== undefined && { reverse: question.reverse }),
          ...(isRange && { max: (question.step_quantity ?? 11) - 1 }),
        },
        origin: grupoSlug || "adulto",
      };
      localStorage.setItem("userAnswers", JSON.stringify(newAnswers));
      return newAnswers;
    });

    // Verificar se pergunta é sobre idade (253) e avaliar se é bebê ou criança
    if (sym_id === 253) {
      if (!answer || answer.trim().length < 10) return; // formato inválido
      const birth = typeof answer === "string" ? answer.trim() : "";
      
      const [day, month, year] = birth.split("/").map(Number);
      const birthDate = new Date(year, month - 1, day);
      const today = new Date();

      if (birthDate > today) {
        toast.warn("A data de nascimento não pode estar no futuro.");
        setAnswers((prev) => {
          const newAnswers = { ...prev };
          delete newAnswers[sym_id];
          return newAnswers;
        });
        return;
      }

      const age = Utils.calculateAge(birth);
      if (grupoSlug === "my-baby" && age >= 3) {
        toast.warn(
          "A idade informada indica que o paciente não é mais um bebê. Por favor, verifique e tente novamente."
        );
        // limpar resposta
        setAnswers((prev) => {
          const newAnswers = { ...prev };
          delete newAnswers[sym_id];
          return newAnswers;
        });
      } else if (grupoSlug === "my-kids" && (age < 3 || age > 12)) {
        toast.warn(
          "Parece que a idade não corresponde ao grupo selecionado. Por favor, verifique e tente novamente."
        );
        setAnswers((prev) => {
          const newAnswers = { ...prev };
          delete newAnswers[sym_id];
          return newAnswers;
        });
      } else if (
        (grupoSlug === "my-baby" || grupoSlug === "my-kids") &&
        age > 12
      ) {
        toast.warn(
          "A idade informada indica que não se trata de bebê ou criança. Vamos direcioná-lo para o questionário correto."
        );
        window.location.href = `${process.env.PUBLIC_URL || ""}${rewriteAdultCollectionUrl("/questionario/adulto")}`;
        setAnswers((prev) => {
          const newAnswers = { ...prev };
          delete newAnswers[sym_id];
          return newAnswers;
        });
      } else if (
        grupoSlug !== "my-baby" &&
        grupoSlug !== "my-kids" &&
        age <= 12
      ) {
        toast.warn(
          "A idade informada indica que não se trata de um adulto. Deve ser respondido o questionário específico para bebês ou crianças. Vamos direcioná-lo para o questionário correto."
        );
        setAnswers((prev) => {
          const newAnswers = { ...prev };
          delete newAnswers[sym_id];
          return newAnswers;
        });
      }
    }

    if (sym_id === 254) {
      // Após salvar a resposta de sexo, recarregar perguntas filtradas corretamente
      setTimeout(() => {
        fetchQuestions();
      }, 0);
    }

    // Aviso para marcação em grávida
    if (sym_id === 472 && (answer == 418 || answer == 537 || answer == 542)) {
      const data = {
          icon: fullLogo,
          title: "Atenção",
          content: {
              description: `Você marcou que está gestante, lactante ou tentante. Esta informação está correta?`,
              button: {
                  label: "Continuar",
                  onClick: () => { closeModal(); }
              }
          }
      }
      openModal('confirmation', data);
    }

    // Lógicas condicionais para Questionário Proteína
    if (sym_id === 264) {
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.sym_id === 265) {
            return { ...q, hidden: answer === 239 }; // só mostra se NÃO for 240
          }
          if (q.sym_id === 266) {
            return { ...q, hidden: false }; // sempre mostra 266
          }
          return q;
        })
      );

      if (answer === 241) {
        window.location.href = `${process.env.PUBLIC_URL || ""}${rewriteAdultCollectionUrl("/questionario/adulto")}`;
      }
    }

    if (sym_id === 266) {
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.sym_id === 267) {
            return { ...q, hidden: answer !== 245 }; // frutado
          }
          if (q.sym_id === 277) {
            return { ...q, hidden: answer !== 246 }; // encorpado
          }
          return q;
        })
      );

      if (answer === 244) {
        setIsChatFinished(true);
      }
    }
  };

  const onAnswerBlur = (sym_id, answer, question) => {
    if (sym_id === 252) { // Nome Completo
      const val = typeof answer === "string" ? answer.trim() : "";
      if (val === "") return;

      const parts = val.split(/\s+/).filter(Boolean);
      if (parts.length < 2) {
        toast.warn("Por favor, Informe seu nome completo.");
        setAnswers((prev) => {
          const newAnswers = { ...prev };
          delete newAnswers[sym_id];
          localStorage.setItem("userAnswers", JSON.stringify(newAnswers));
          return newAnswers;
        });
      }
    }
  };

  const handleGroupedAnswerSubmission = (groupAnswers) => {
    const grouped = currentGrouped?.questions || [];

    setAnswers((prev) => {
      const updated = {
        ...prev,
        origin: grupoSlug || "adulto",
      };

      grouped.forEach((q) => {
        const a = groupAnswers[q.sym_id];
        if (a !== undefined) {
          updated[q.sym_id] = {
            answer: { sym_id: q.sym_id, answer: a },
            group: q.groupId,
            subject: q.sub_id,
            sym_for: q.sym_for,
            sym_group: q.sym_group ?? null,
            ...(q.reverse !== undefined && { reverse: q.reverse }),
          };
        }
      });

      localStorage.setItem("userAnswers", JSON.stringify(updated));
      return updated;
    });
  };

  // Valida a seção atual e, se for a última, finaliza. Retorna true se ok, false se erro.
  const { user } = useContext(UserContext);
  const validateSectionAndFinalizeIfLast = async (isFinalStep = false, setIsProcessing) => {
    // LOGS para depuração
    console.log('[validateSectionAndFinalizeIfLast] index:', currentSectionIndex, 'sectionOrder:', sectionOrder, 'isFinalStep:', isFinalStep);
  if (isFinalStep) setIsChatFinished(false); // resetar flag só no final
    const storedAnswers = JSON.parse(
      localStorage.getItem("userAnswers") || "{}"
    );

    let error = null;
    let errorQuestionId = null;

    const focusQuestionField = (symId) => {
      if (!symId || typeof document === "undefined") return;
      window.setTimeout(() => {
        const root = document.querySelector(`[data-question-sym-id="${symId}"]`);
        if (!root) return;
        root.scrollIntoView({ behavior: "smooth", block: "center" });
        const field = root.querySelector("input, textarea, select, button");
        if (field && typeof field.focus === "function") {
          field.focus({ preventScroll: true });
        }
      }, 0);
    };

    if (!Array.isArray(sectionOrder) || sectionOrder.length === 0) {
      toast.error("Erro interno: ordem das seções não carregada.");
      return false;
    }
    const currentSectionId = sectionOrder[currentSectionIndex];
    const currentSection = questions.filter(
      (q) => String(q.sub_id) === String(currentSectionId)
    );

    if (!currentSectionQuestions || !Array.isArray(currentSectionQuestions)) {
      toast.error("Erro interno: perguntas da seção não carregadas.");
      return false;
    }

    for (const question of currentSectionQuestions) {
      const answer = storedAnswers[question.sym_id]?.answer || "";
      if (question.sub_id === 38 && !question.optional) {
        if (question.sym_id === 252) {
          const parts = String(answer)
            .trim()
            .split(/\s+/)
            .filter(Boolean);
          if (parts.length < 2) {
            error = "Informe nome e sobrenome.";
            errorQuestionId = question.sym_id;
            break;
          }
        }
        if (question.sym_id === 253) {
          if (answer.length < 10) {
            error = "A data de nascimento não está correta.";
            errorQuestionId = question.sym_id;
            break;
          }
          const [d, m, y] = String(answer).split("/").map(Number);
          const bDate = new Date(y, m - 1, d);
          if (bDate > new Date()) {
            error = "A data de nascimento não pode estar no futuro.";
            errorQuestionId = question.sym_id;
            break;
          }
        }
        if (question.sym_id === 254 && answer.length < 2) {
          error = "Informe seu sexo.";
          errorQuestionId = question.sym_id;
          break;
        }
        if (question.sym_id === 255 && answer.length < 1) {
          error = "Informe o peso";
          errorQuestionId = question.sym_id;
          break;
        }
        if (question.sym_id === 256 && answer.length < 2) {
          error = "Altura precisa ter pelo menos 2 caracteres.";
          errorQuestionId = question.sym_id;
          break;
        }
      }
    }

    // Validação geral de obrigatórias
    if (!error) {
      if (currentGrouped) {
        const obrigatorias = currentGrouped.questions.filter(
          (q) => !q.optional && q.type !== "separatorTitle"
        );
        const respondidas = obrigatorias.filter((q) => {
          const answerData = answers[q.sym_id];
          if (Array.isArray(answerData)) return answerData.length > 0;
          if (typeof answerData?.answer === "string") {
            return answerData.answer.trim() !== "";
          }
          if (
            typeof answerData?.answer === "number" ||
            typeof answerData?.answer === "boolean"
          ) {
            return (
              answerData.answer !== null && answerData.answer !== undefined
            );
          }
          if (typeof answerData === "object" && answerData !== null) {
            const hasIndexedKeys = Object.keys(answerData).some(
              (key) => !isNaN(Number(key))
            );
            return hasIndexedKeys;
          }
          return false;
        });
        // if (respondidas.length !== obrigatorias.length) {
        //     error = "Responda todas as perguntas obrigatórias da seção.";
        // }
      } else if (currentSection?.length > 0) {
        const obrigatorias = currentSection.filter((q) => {
          if (q.sym_id === 355) return false;
          if (q.sym_id === 356) return false;
          if (q.sym_id === 357) return false;
          if (q.sym_id === 358) return false;
          return !q.optional && q.type !== "separatorTitle";
        });
        const respondidas = obrigatorias.filter((q) => {
          const answerData = answers[q.sym_id];
          if (Array.isArray(answerData)) return answerData.length > 0;
          if (typeof answerData?.answer === "string") {
            return answerData.answer.trim() !== "";
          }
          if (
            typeof answerData?.answer === "number" ||
            typeof answerData?.answer === "boolean"
          ) {
            return (
              answerData.answer !== null && answerData.answer !== undefined
            );
          }
          if (typeof answerData === "object" && answerData !== null) {
            const hasIndexedKeys = Object.keys(answerData).some(
              (key) => !isNaN(Number(key))
            );
            return hasIndexedKeys;
          }
          return false;
        });
        
      }
    }

    // Validação mínima por seção
    if (!error) {
      const minSectionResult = validateMinSectionAnswer({
        sectionId: currentSectionId,
        sectionQuestions: currentSectionQuestions,
        currentGrouped,
        answers,
        storedAnswers,
      });
      if (minSectionResult.error) {
        error = minSectionResult.error;
        errorQuestionId = minSectionResult.errorQuestionId;
      }
    }

    if (error) {
      focusQuestionField(errorQuestionId);
      toast.warn(error);
      return false;
    }

    // Sempre salva ao finalizar a última seção, independente do tamanho de sectionOrder
    if (
      isFinalStep &&
      Array.isArray(sectionOrder) &&
      currentSectionIndex === sectionOrder.length - 1
    ) {
      try {
        const storedAnswers = JSON.parse(
          localStorage.getItem("userAnswers") || "{}"
        );
        const origin = storedAnswers?.origin || grupoSlug || "adulto";
        if (origin === "my-baby") {
          const result = evaluateBaby({ answers: storedAnswers, questions });
          localStorage.setItem("babyEvaluation", JSON.stringify(result));
        } else if (origin === "my-kids") {
          const result = evaluateKids({ answers: storedAnswers, questions });
          localStorage.setItem("kidsEvaluation", JSON.stringify(result));
        } else {
          const result = evaluateAdults({ answers: storedAnswers, questions });
          localStorage.setItem("adultEvaluation", JSON.stringify(result));
        }
        // Salvar no banco de dados ao finalizar
        const usr_id = storedAnswers?.usr_id || user?.usr_id || null;
        if (typeof setIsProcessing === 'function') setIsProcessing(true);
        await registerUserWithAnswers({ usr_id, setIsProcessing: setIsProcessing || (()=>{}) });
      } catch (e) {
        console.error("evaluate error:", e);
      }
      setIsChatFinished(true);
    }
    setAnalyzing(false);
    return true;
  };

  const goBackToPreviousSection = () => {
    const prevIndex = currentSectionIndex - 1;
    if (prevIndex >= 0) {
      window.scrollTo(0, 0);
      setCurrentSectionIndex(prevIndex);
      const prevSectionId = sectionOrder[prevIndex];
      navigate(`/questionario/${grupoSlug}/${prevSectionId}`);
    }
  };

  // Função para avançar para próxima seção e atualizar URL
  const goToNextSection = async (setIsProcessing) => {
    if (currentSectionIndex < sectionOrder.length - 1) {
      const nextIndex = currentSectionIndex + 1;
      setCurrentSectionIndex(nextIndex);
      window.scrollTo(0, 0);
      const nextSectionId = sectionOrder[nextIndex];
      navigate(`/questionario/${grupoSlug}/${nextSectionId}`);
    } else {
      // Última seção, salva e navega para o Mapa
      try {
        const storedAnswers = JSON.parse(localStorage.getItem("userAnswers") || "{}")
        const origin = storedAnswers?.origin || grupoSlug || "adulto";
        if (origin === "my-baby") {
          const result = evaluateBaby({ answers: storedAnswers, questions });
          localStorage.setItem("babyEvaluation", JSON.stringify(result));
        } else if (origin === "my-kids") {
          const result = evaluateKids({ answers: storedAnswers, questions });
          localStorage.setItem("kidsEvaluation", JSON.stringify(result));
        } else {
          const result = evaluateAdults({ answers: storedAnswers, questions });
          localStorage.setItem("adultEvaluation", JSON.stringify(result));
        }
        const usr_id = storedAnswers?.usr_id || user?.usr_id || null;
        if (typeof setIsProcessing === 'function') setIsProcessing(true);
        await registerUserWithAnswers({ usr_id, setIsProcessing: setIsProcessing || (()=>{}) });
      } catch (e) {
        console.error("Erro ao salvar anamnese no goToNextSection:", e);
      }
      setIsChatFinished(true);
      // navigate(`/mapa-da-saude`);
    }
  };

  return {
    currentSectionIndex,
    currentSectionQuestions,
    currentGrouped,
    handleChangeAnswer,
    handleBlurAnswer: onAnswerBlur,
    handleGroupedAnswerSubmission,
    validateSectionAndFinalizeIfLast,
    goBackToPreviousSection,
    goToNextSection,
    isChatFinished,
    currentSectionMeta,
    progress,
    answers,
    performance,
    weightLoss,
    isLoaded,
    analyzing,
  };
};

export default useChatLogic;
